/// <amd-module name="Dashboards/WidgetCatalog/WidgetCatalogQuery" />
import { IVssPageContext } from "VSS/Platform/Context";
import { Uri } from "VSS/Platform/Util/Url";

import { WidgetMetadata, WidgetScope, WidgetTypesResponse } from "Dashboards/Common/Contracts/Dashboard";
import { IDashboardRestClient } from "Dashboards/Common/RestClient/Dashboard";
import * as Resources from "Dashboards/WidgetCatalog/Resources";
import { WidgetCatalogEntry } from "Dashboards/WidgetContracts/Internal/WidgetCatalogEntry";
import { ICacheableQuery } from "Reporting/Common/ICacheableQuery";

/** A description of a Widget Catalog. Describes widgets available in a particular project. */
export interface WidgetCatalogInformation {
    catalogEntries: WidgetCatalogEntry[];
    galleryUrl: string;
}

/** Provides a cacheable instance of an internal catalog query for sharing across widgets & edit consumers */
export class WidgetCatalogQuery implements ICacheableQuery<WidgetCatalogInformation> {
    public constructor(private projectId: string | undefined) {}

    public getKey(): string {
        const projectId = this.projectId != null ? this.projectId.toLowerCase() : "Unscoped";
        return "WidgetCatalogQuery." + projectId;
    }

    public runQuery(pageContext: IVssPageContext): Promise<WidgetCatalogInformation> {
        return getWidgetCatalogEntries(pageContext, this.projectId);
    }
}

const approvedSchemes = ["http", "https"];
/** Checks if the given URL uses an approved protocol scheme for the widget catalog. */
function usesApprovedUrlScheme(url: string): boolean {
    try {
        const scheme = new Uri(url).scheme;
        return approvedSchemes.indexOf(scheme) >= 0;
    } catch {
        return false;
    }
}

/**
 * Makes an Async request to the Dashboard service for a specific widget Type, which includes legacy information not available under New platform client APIs.
 * This main use case is for identifying a legacy Configuration contribution of arbitrary widget Types.
 * The same information is available from Widget properties of a dashboard, which is a much more efficient way to get at it.
 */
function getWidgetCatalogEntries(pageContext: IVssPageContext, projectId: string | undefined): Promise<WidgetCatalogInformation> {
    const client = pageContext.getRestClient<IDashboardRestClient>("IDashboardRestClient");

    // Make a request to Dashboard service for Widget Types.
    return client.getWidgetTypes(WidgetScope.Project_Team, projectId).then((response: WidgetTypesResponse) => {
        return {
            catalogEntries: response.widgetTypes.map((type: WidgetMetadata) => {
                const sortedSizes =
                    !type.allowedSizes || type.allowedSizes.length === 0
                        ? [{ columnSpan: 1, rowSpan: 1 }]
                        : type.allowedSizes.slice().sort((a, b) => {
                              return a.columnSpan - b.columnSpan || a.rowSpan - b.rowSpan;
                          });

                const catalogEntry: WidgetCatalogEntry = {
                    canStoreCrossProjectSettings: true,
                    catalogIconUrl: type.catalogIconUrl,
                    catalogInfoURI: usesApprovedUrlScheme(type.catalogInfoUrl) ? type.catalogInfoUrl : "",
                    configContributionId: type.configurationContributionId,
                    contributionId: type.contributionId,
                    defaultSize: type.allowedSizes && type.allowedSizes.length > 0 ? type.allowedSizes[0] : sortedSizes[0],
                    description: type.description,
                    isNameConfigurable: type.isNameConfigurable,
                    isVisibleFromCatalog: type.isVisibleFromCatalog,
                    keywords: type.keywords,
                    lightbox: type.lightboxOptions,
                    name: type.name,
                    publisherName: type.publisherName === "ms" ? Resources.MicrosoftContributionPublisherName : type.publisherName,
                    supportedSizes: sortedSizes,
                    tags: type.tags
                };

                return catalogEntry;
            }),
            // In event of gallery Service being unavailable, the gallery url will just be dropped, rather than crashing the entire experience.
            galleryUrl: response._links && response._links["web"] && response._links["web"].href
        };
    });
}
